import { adminClient, inferAdditionalFields } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

/**
 * The single Better Auth client factory for every Routly web surface.
 *
 * This used to be copy-pasted between the main app and the admin console, and
 * the copies had already drifted: each carried a comment describing a different
 * symptom (`/api/sign-in/email` vs `/api/get-session`) of the same underlying
 * bug, and only one copy exported the `SessionUser` type. The next auth-base
 * fix would land in one app and silently miss the other.
 *
 * Each app still bakes its own `VITE_API_URL` at build time (the values differ
 * per app), so the module exports a factory and each app's thin
 * `lib/auth-client.ts` shim passes its own env value through.
 */
export function createRoutlyAuthClient(apiUrl: string | undefined) {
  const trimmed = apiUrl?.trim();
  const apiBase = trimmed && trimmed.length > 0 ? trimmed : '/api';
  const apiOrigin = apiBase.startsWith('http') ? apiBase : `${window.location.origin}${apiBase}`;

  // The full auth base, `/auth` included, rather than baseURL + basePath. When
  // baseURL already carries a path (here `/api`), better-auth treats that path
  // as the base and does NOT append basePath, so requests would go to
  // `/api/sign-in/email` instead of `/api/auth/sign-in/email`. The backend
  // mounts the auth handler at `/auth/*` and its session guard only excludes
  // `/auth`, so every such request was rejected with 401 and login could never
  // succeed.
  const baseURL = `${apiOrigin.replace(/\/+$/, '')}/auth`;

  return createAuthClient({
    baseURL,
    // Look `fetch` up per call instead of letting better-auth capture it when
    // the client is created. The demo's fallback mock layer replaces
    // `globalThis.fetch` after the module graph has loaded, and a captured
    // reference skips it — sign-in went to the network and 404ed in any browser
    // that refuses a service worker. The hosted demo never saw this because a
    // worker intercepts below fetch entirely. Nothing else patches fetch, so in
    // production this resolves to the same function it always did.
    fetchOptions: {
      customFetchImpl: (input, init) => globalThis.fetch(input, init),
    },
    plugins: [
      adminClient(),
      inferAdditionalFields({
        user: {
          organizationId: { type: 'string' },
          isActive: { type: 'boolean' },
        },
      }),
    ],
  });
}

export type RoutlyAuthClient = ReturnType<typeof createRoutlyAuthClient>;

export type SessionUser = NonNullable<ReturnType<RoutlyAuthClient['useSession']>['data']>['user'];
